import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SuccessPage.module.css";

interface SuccessPageProps {
    applicationId?: string;
    contactEmail?: string;
    onDashboardClick?: () => void;
}

export const SuccessPage: React.FC<SuccessPageProps> = ({ applicationId, contactEmail, onDashboardClick }) => {
    const navigate = useNavigate();

    const handleDashboard = () => {
        if (onDashboardClick) {
            onDashboardClick();
        } else {
            navigate("/dashboard");
        }
    };
    const handleBackToLogin = useCallback(() => {
        // Clear all persisted data before navigating to login
        // resetForm();
        // Navigate to login page
        window.location.href = "/login";
    }, []);

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <button
                    onClick={handleBackToLogin}
                    className={styles.backLink}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                    <span>←</span>
                    <span>Back to Login</span>
                </button>
                <div className={styles.card}>
                    {/* Success Icon with Animation */}
                    <div className={styles.iconContainer}>
                        <div className={styles.clockIcon}>
                            <svg fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                            </svg>
                        </div>
                        <div className={styles.checkIcon}>
                            <svg fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                            </svg>
                        </div>
                    </div>

                    {/* Title */}
                    <h1 className={styles.title}>Application Submitted Successfully!</h1>

                    <p className={styles.subtitle}>Thank you for registering with VIN Pulse OS</p>

                    {/* Account Under Review Box */}
                    <div className={styles.reviewBox}>
                        <div className={styles.reviewIcon}>
                            <svg fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className={styles.reviewContent}>
                            <h3 className={styles.reviewTitle}>Account Under Review</h3>
                            <p className={styles.reviewText}>
                                Your account and documents are currently being reviewed by our team. This process
                                typically takes 1-2 business days.
                            </p>
                            <div className={styles.reviewList}>
                                <p className={styles.reviewListTitle}>What's being reviewed:</p>
                                <ul>
                                    <li>Business information and LLC details</li>
                                    <li>Tax ID and Federal Identification documents</li>
                                    <li>Tobacco license verification</li>
                                    <li>ACH banking information</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* What Happens Next */}
                    <div className={styles.nextStepsContainer}>
                        <h2 className={styles.nextStepsTitle}>What happens next?</h2>
                        <div className={styles.stepsList}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div>
                                    <h4 className={styles.stepTitle}>Document Verification</h4>
                                    <p className={styles.stepDescription}>
                                        Our team will verify all submitted documents and business information.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div>
                                    <h4 className={styles.stepTitle}>Credit Check & Approval</h4>
                                    <p className={styles.stepDescription}>
                                        We'll conduct a standard credit check and review your account for approval.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <div className={styles.stepNumber}>3</div>
                                <div>
                                    <h4 className={styles.stepTitle}>Account Activation</h4>
                                    <p className={styles.stepDescription}>
                                        Once approved, you'll receive an email with login credentials and next steps.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Confirmation Details */}
                    <div className={styles.confirmationBox}>
                        <h3 className={styles.confirmationTitle}>Confirmation Details</h3>

                        {contactEmail && (
                            <div className={styles.confirmationItem}>
                                <svg className={styles.confirmationIcon} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                <div>
                                    <p className={styles.confirmationLabel}>Confirmation sent to</p>
                                    <p className={styles.confirmationValue}>{contactEmail}</p>
                                </div>
                            </div>
                        )}

                        {applicationId && (
                            <div className={styles.confirmationItem}>
                                <svg className={styles.confirmationIcon} fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <div>
                                    <p className={styles.confirmationLabel}>Submitted on</p>
                                    <p className={styles.confirmationValue}>
                                        {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Need Help Section */}
                    <div className={styles.helpBox}>
                        <h3 className={styles.helpTitle}>Need Help?</h3>
                        <p className={styles.helpText}>
                            If you have any questions about your application or need to update any information, please
                            contact our support team.
                        </p>
                        <div className={styles.helpButtons}>
                            <button
                                className={styles.helpButton}
                                onClick={() => (window.location.href = "mailto:info@vindistro.com")}
                            >
                                ✉ Email Support
                            </button>
                            <button
                                className={styles.helpButton}
                                onClick={() => (window.location.href = "tel:5028074201")}
                            >
                                ☎ Call Us
                            </button>
                        </div>
                    </div>

                    {/* Action Button */}
                    <div className={styles.actionButtonContainer}>
                        <button onClick={handleDashboard} className={styles.actionButton}>
                            ✓ Go to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
